/**
 * `courseTime` is the course's total duration in minutes across all lessons —
 * not a time of day, and not capped at a day: a course can run over several.
 * The limit is a guard against typos (3000 for 300), and matches
 * MAX_COURSE_TIME_MINUTES in the API.
 */
export const MAX_COURSE_DURATION_MINUTES = 30 * 24 * 60;

/** The same limit in the unit the hours field is typed in. */
export const MAX_COURSE_DURATION_HOURS = MAX_COURSE_DURATION_MINUTES / 60;

/** "3 t. 30 min." — the caseworker reads hours off the certificate. */
export const formatCourseDuration = (
  minutes: number | null | undefined,
): string => {
  if (minutes == null) return "-";

  const wholeHours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (wholeHours === 0) return `${remainingMinutes} min.`;
  if (remainingMinutes === 0) return `${wholeHours} t.`;

  return `${wholeHours} t. ${remainingMinutes} min.`;
};

/**
 * Formik replaces the string value of an `<input type="number">` with a real
 * number on change (`parseFloat`, or `""` when unparsable), so this accepts
 * both shapes — reading it as a string alone crashed on `value.trim()` as soon
 * as the caseworker typed a digit.
 */
export const formValueToCourseDuration = (
  value: string | number | null | undefined,
): number | null => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;

  return value.trim() === "" ? null : Number(value);
};

/**
 * The duration split into the two fields the caseworker types into.
 *
 * A certificate states hours, so typing 2880 minutes for a two-day course is
 * arithmetic the caseworker should not have to do. Long courses stay in hours
 * rather than gaining a days field: a "kursusdag" has no fixed length.
 */
export const courseDurationToParts = (
  minutes: number | null | undefined,
): { hours: string; minutes: string } => {
  if (minutes == null) return { hours: "", minutes: "" };

  return {
    hours: String(Math.floor(minutes / 60)),
    minutes: String(minutes % 60),
  };
};

/**
 * The two fields combined into total minutes, or null when both are blank so
 * the duration can still be cleared.
 *
 * A blank alongside a filled field counts as zero — someone entering a round
 * "3 t." should not have to type a 0 into the minutes field. Minutes past 59
 * carry into the hours rather than being rejected, so a certificate stating
 * "90 minutter" can be typed as read.
 */
export const partsToCourseDuration = (
  hours: string | number | null | undefined,
  minutes: string | number | null | undefined,
): number | null => {
  const parsedHours = formValueToCourseDuration(hours);
  const parsedMinutes = formValueToCourseDuration(minutes);

  if (parsedHours == null && parsedMinutes == null) return null;

  const isBlank = (value: string | number | null | undefined): boolean =>
    value == null || (typeof value === "string" && value.trim() === "");

  // Only a blank counts as zero. Unparsable input stays null so validation can
  // report it rather than silently saving a shorter course.
  if (parsedHours == null && !isBlank(hours)) return null;
  if (parsedMinutes == null && !isBlank(minutes)) return null;

  return (parsedHours ?? 0) * 60 + (parsedMinutes ?? 0);
};
